/*
  ITGC Line editor modal — add or edit a single line within a request.

  Conditional flow (driven by the parent's locked target user):
    1. Action  (GRANT / REVOKE)   — chosen FIRST; drives eligibility
    2. Type    (Application / Asset)
    3. Target  — disabled until Action + Type set; then shows ONLY targets the
                 backend deems eligible for (user, action), minus targets already
                 added as pending lines on this request.
    4. Due by, Valid from/until (GRANT only), Remarks.

  No user picker — the target user is set at the request header by the parent.
*/
import React, { useEffect, useState } from 'react';
import { Save, Check, CircleMinus, Edit2 } from 'lucide-react';
import {
  itgcService, AccessLineCreatePayload, EligibleTargets, TargetType, LineAction,
} from '../../services/itgcService';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedDatePicker } from '../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { swalConfirm } from '../common/confirm';
import extractApiError from '../../utils/apiError';

export interface LineDraft extends AccessLineCreatePayload {
  _key: number;
  _existingId?: number;
  is_active?: number;  // 1 = active, -1/0 = inactive
}

interface ExistingTarget {
  target_type: TargetType;
  target_id: number;
  action: LineAction;
}

interface Props {
  initial?: LineDraft | null;
  /** Display label for the locked target when editing a saved line (its eligible-list
      entry is gone once granted, so the parent supplies the label to render). */
  initialTargetLabel?: string;
  /** Locked target user from the request header — eligibility is computed against this user. */
  targetUserId: number;
  /** Lines already on this request (excluding the one being edited) — excluded from the dropdown. */
  existingTargets: ExistingTarget[];
  onClose: () => void;
  onSave: (line: LineDraft) => void;
  /** Called after a saved line's active/inactive state was persisted; parent reloads. */
  onToggledActive?: () => void;
}

const blank = (): LineDraft => ({
  _key: Math.random(),
  target_user_id: 0,                 // filled in by parent at submit time
  target_type: 'APPLICATION',
  target_id: 0,
  action: 'GRANT',
  due_date: '',
  valid_from: '',
  valid_until: '',
  line_remarks: '',
});

const ITGCLineModal: React.FC<Props> = ({
  initial, initialTargetLabel, targetUserId, existingTargets, onClose, onSave, onToggledActive,
}) => {
  const vh = useFieldValidation();
  const { allowed: canModify, loading: permLoading } = usePermission('ORG_ITGC_ACCESS_REQUEST', 'view+modify');
  const [draft, setDraft] = useState<LineDraft>(() => initial ? { ...initial } : blank());
  const [eligible, setEligible] = useState<EligibleTargets>({ applications: [], assets: [] });
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [editMode, setEditMode] = useState(false);  // view-first: start in view mode for existing lines
  const [toggling, setToggling] = useState(false);

  const isEdit = !!initial?._existingId;       // a saved line — target/action locked
  const targetLocked = isEdit;
  const fieldsDisabled = isEdit && !editMode;  // for existing lines, disable fields until Edit is clicked
  const isActive = initial?.is_active === 1;   // track if line is currently active

  useEffect(() => { vh.reset(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch eligible targets whenever (user, action) changes. Editing a saved line
  // keeps its locked target, so no fetch is needed there.
  useEffect(() => {
    if (targetLocked || !targetUserId || !draft.action) return;
    let cancelled = false;
    setLoadingTargets(true);
    itgcService.eligibleTargets(targetUserId, draft.action)
      .then(res => { if (!cancelled) setEligible(res); })
      .catch((e: any) => { if (!cancelled) setToast({ type: 'error', message: e?.message || 'Failed to load eligible targets' }); })
      .finally(() => { if (!cancelled) setLoadingTargets(false); });
    return () => { cancelled = true; };
  }, [targetUserId, draft.action, targetLocked]);

  const set = (patch: Partial<LineDraft>) => setDraft(d => ({ ...d, ...patch }));

  const validate = (): string | null => {
    if (!draft.target_id) return `Pick a ${draft.target_type.toLowerCase()}`;
    if (draft.action === 'GRANT'
      && draft.valid_from && draft.valid_until
      && draft.valid_until < draft.valid_from) {
      return 'Valid until must be on or after valid from';
    }
    if (draft.action === 'GRANT'
      && draft.due_date && draft.valid_until
      && draft.valid_until < draft.due_date) {
      return 'Valid until must be on or after due by';
    }
    return null;
  };

  const save = () => {
    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    const fmtOk = vh.validateAll([
      { name: 'line_remarks', code: 110, value: draft.line_remarks },
      { name: 'valid_until', code: 305, value: draft.valid_until },
    ]);
    if (!fmtOk) { setToast({ type: 'error', message: 'Please fix the highlighted fields' }); return; }
    const err = validate();
    if (err) { setToast({ type: 'error', message: err }); return; }
    onSave(draft);
  };

  // Persist the active/inactive flag for a saved line, after a confirmation.
  const handleToggleActive = async () => {
    if (!initial?._existingId) return;
    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    const makeInactive = isActive;   // currently active -> deactivate, else reactivate
    const ok = await swalConfirm({
      title: makeInactive ? 'Inactivate this line?' : 'Activate this line?',
      text: makeInactive
        ? 'It will move to the inactive list and stop counting as an active line.'
        : 'It will move back to the active list.',
    } as any);
    if (!ok) return;
    setToggling(true);
    try {
      await itgcService.setLineActive(initial._existingId, makeInactive ? 0 : 1);
      onToggledActive?.();
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Failed to update line') });
    } finally {
      setToggling(false);
    }
  };

  // Targets already used (same action) on this request — excluded so the same
  // grant/revoke can't be added twice in one request.
  const usedKey = (t: { target_type: TargetType; target_id: number }) => `${t.target_type}:${t.target_id}`;
  const usedSet = new Set(
    existingTargets.filter(t => t.action === draft.action).map(usedKey),
  );

  let targetOptions: { value: string; label: string }[];
  if (targetLocked) {
    // Saved line: its target is no longer in the eligible list (already granted).
    // Render the single locked option so its label still shows.
    targetOptions = [{ value: String(draft.target_id), label: initialTargetLabel || `#${draft.target_id}` }];
  } else if (draft.target_type === 'APPLICATION') {
    targetOptions = eligible.applications
      .filter(a => !usedSet.has(`APPLICATION:${a.id}`))
      .map(a => ({ value: String(a.id), label: a.label }));
  } else {
    targetOptions = eligible.assets
      .filter(a => !usedSet.has(`ASSET:${a.id}`))
      .map(a => ({ value: String(a.id), label: a.label }));
  }

  const actionOptions = [
    { value: 'GRANT', label: 'GRANT' },
    { value: 'REVOKE', label: 'REVOKE' },
  ];
  const typeOptions = [
    { value: 'APPLICATION', label: 'Application' },
    { value: 'ASSET', label: 'Asset' },
  ];

  const targetDisabled = targetLocked || !targetUserId || loadingTargets;
  const noEligible = !loadingTargets && !targetLocked && targetOptions.length === 0;
  const verb = draft.action === 'GRANT' ? 'grant' : 'revoke';
  const noun = draft.target_type === 'APPLICATION' ? 'applications' : 'assets';
  const targetHelper = !targetUserId
    ? 'Select the target user first.'
    : loadingTargets
      ? 'Loading eligible targets…'
      : noEligible
        ? `No eligible ${noun} to ${verb} for this user.`
        : undefined;

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="md">
        <Modal.Header>{isEdit ? 'Edit Line' : 'Create Line'}</Modal.Header>

        <Modal.Body>
          <form id="itgc-line-form"
            className="grid grid-cols-2 gap-x-6 gap-y-4"
            data-testid="MOD_itgc_line_form">

            {/* 1. Action — chosen first; drives eligibility */}
            <FormControl.SearchSelect
              label="Action"
              required
              value={draft.action}
              onChange={value => set({ action: value as LineAction, target_id: 0 })}
              options={actionOptions}
              disabled={targetLocked || fieldsDisabled}
              data-testid="SEL_itgc_line_action"
            />

            {/* 2. Type */}
            <FormControl.SearchSelect
              label="Type"
              required
              value={draft.target_type}
              onChange={value => set({ target_type: value as TargetType, target_id: 0 })}
              options={typeOptions}
              disabled={targetLocked || fieldsDisabled}
              data-testid="SEL_itgc_line_type"
            />

            {/* 3. Target — gated on Action + Type + eligible list */}
            <FormControl.SearchSelect
              label="Target"
              required
              value={String(draft.target_id || '')}
              onChange={value => set({ target_id: Number(value) })}
              options={targetOptions}
              disabled={targetDisabled || fieldsDisabled}
              placeholder={targetLocked ? undefined : '— pick —'}
              helper={targetHelper}
              data-testid="SEL_itgc_line_target"
            />

            {/* 4. Due by */}
            <FormControl.DatePicker
              label="Due by"
              value={draft.due_date || ''}
              onChange={e => set({ due_date: e.target.value })}
              disabled={fieldsDisabled}
              data-testid="INP_itgc_line_due_date"
            />

            {draft.action === 'GRANT' && (
              <>
                <FormControl.DatePicker
                  label="Valid from"
                  value={draft.valid_from || ''}
                  onChange={e => set({ valid_from: e.target.value })}
                  disabled={fieldsDisabled}
                  data-testid="INP_itgc_line_valid_from"
                />
                <ValidatedDatePicker
                  vh={vh}
                  name="valid_until"
                  code={305}
                  label="Valid until"
                  value={draft.valid_until || ''}
                  onChange={e => set({ valid_until: e.target.value })}
                  disabled={fieldsDisabled}
                  data-testid="INP_itgc_line_valid_until"
                />
              </>
            )}

            <div className="col-span-2">
              <ValidatedInput
                vh={vh}
                name="line_remarks"
                code={110}
                label="Remarks"
                value={draft.line_remarks || ''}
                onChange={e => set({ line_remarks: e.target.value })}
                disabled={fieldsDisabled}
                data-testid="INP_itgc_line_remarks"
              />
            </div>
          </form>
        </Modal.Body>

        <Modal.Footer className={isEdit && onToggledActive ? 'app-modal-footer--split' : ''}>
          {isEdit && onToggledActive && (
            <Button
              variant={isActive ? 'inactive-outline' : 'active-outline'}
              icon={CircleMinus}
              type="button"
              onClick={handleToggleActive}
              disabled={permLoading || !canModify || toggling}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
              data-testid="BTN_itgc_line_toggle_active"
            >
              {toggling ? '…' : isActive ? 'Inactive' : 'Active'}
            </Button>
          )}
          {isEdit && !editMode ? (
            <Button
              variant="fill"
              icon={Edit2}
              type="button"
              onClick={() => setEditMode(true)}
              disabled={permLoading || !canModify}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
              data-testid="BTN_itgc_line_edit"
            >
              Edit
            </Button>
          ) : (
            <Button
              type="button"
              variant={isEdit ? 'update' : 'fill'}
              icon={isEdit ? Check : Save}
              onClick={save}
              disabled={permLoading || !canModify}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
              data-testid="BTN_itgc_line_submit"
            >
              {isEdit ? 'Update' : 'Submit'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

// Helpers exposed for parent rendering
export const targetLabelFor = (
  draft: { target_type: TargetType; target_id: number },
  apps: { id: number; application_name: string }[],
  assets: { id: number; asset_code: string; asset_name: string }[],
): string => {
  if (!draft.target_id) return '—';
  if (draft.target_type === 'APPLICATION') {
    const a = apps.find(x => x.id === draft.target_id);
    return a ? a.application_name : `#${draft.target_id}`;
  }
  const s = assets.find(x => x.id === draft.target_id);
  return s ? `${s.asset_code} — ${s.asset_name}` : `#${draft.target_id}`;
};

export default ITGCLineModal;
